// Libs
import { FC, ReactNode, useEffect } from 'react';
import {
  matchPath,
  NavLink,
  useLocation,
  useParams,
  useSearchParams
} from 'react-router';
import cn from 'classnames';

// Components
import Container from '~fpu/shared/components/Container';
import Header, { RoleTagText } from '~fpu/shared/components/navigation/Header';
import SideNavigation from '~fpu/shared/components/navigation/SideNavigation';
import PageFooter from '~fpu/shared/components/PageFooter';

// RDS
import Icon from '@ramsey-design-system/icon';
import Tag from '@ramsey-design-system/tag';
import {
  AwardIcon,
  ChevronDownIcon,
  DocumentIcon,
  EmailIcon,
  EverydollarIcon,
  FinancialPeaceIcon,
  GroupIcon,
  HeartIcon,
  HelpIcon,
  HomeIcon,
  LightningIcon,
  LinkExternalIcon,
  PersonIcon,
  PlusIcon
} from '@ramsey-design-system/icons';

// Hooks
import { useUserQuery } from '~fpu/shared/global-queries';
import { useBudgetWorkshopEnabled } from '~fpu/modules/FpuDashboard/hooks/useBudgetWorkshopEnabled';
import { useHasRamseyPlusAccess } from '~fpu/shared/hooks/useHasRamseyPlusAccess';

// Utils
import { EVERYDOLLAR, PATHS, RPWA } from '~fpu/config/constants';
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import {
  isCoordinator,
  isOrganizationAdmin,
  isSupportTeam
} from '~fpu/shared/utils/user-class-authorities';
import { PageAnalyticsWrapper } from '~fpu/shared/utils/PageAnalyticsWrapper';
import {
  navGroupExpandedAtom,
  orgIdAtom
} from '~fpu/modules/FpuDashboard/utils/atoms';
import { useAtom } from 'jotai';
import { useZendeskWidget } from '~fpu/shared/hooks/useZendeskWidget';
import useHasNewArticles from './WhatsNew/hooks/useHasNewArticles';

// Assets
import RamseyPlusLogo from '~fpu/assets/ramsey-plus-r.png';

type NavGroup =
  'top' | 'Manage' | 'Results' | 'Resources' | 'Products' | 'bottom';

interface NavItem {
  path: string;
  name: string;
  icon?: FC;
  iconSrc?: string;
  iconClassName?: string;
  largeIcon?: boolean;
  group: NavGroup;
  end?: boolean;
  isNew?: boolean;
  externalLink?: boolean;
  target?: string;
}

const COLLAPSIBLE_GROUPS = [
  'Manage',
  'Results',
  'Resources',
  'Products'
] as const;

const GROUP_LABELS: Record<(typeof COLLAPSIBLE_GROUPS)[number], string> = {
  Manage: 'Manage',
  Results: 'Results',
  Resources: 'Resources',
  Products: 'My Products'
};

const DASHBOARD_NAVIGATION_ITEMS: NavItem[] = [
  {
    path: PATHS.FPU_DASHBOARD.ORGANIZATION,
    name: 'Home',
    icon: HomeIcon,
    group: 'top',
    end: true // https://reactrouter.com/en/main/components/nav-link#end
  },
  {
    path: PATHS.FPU_DASHBOARD.CLASSES,
    name: 'My Classes',
    icon: GroupIcon,
    group: 'Manage'
  },
  {
    path: PATHS.FPU_DASHBOARD.MY_PEOPLE,
    name: 'My People',
    icon: PersonIcon,
    group: 'Manage'
  },
  {
    path: PATHS.FPU_DASHBOARD.SEND_MEMBERSHIPS,
    name: 'Send Memberships',
    icon: EmailIcon,
    group: 'Manage'
  },
  {
    path: PATHS.FPU_DASHBOARD.BUDGET_WORKSHOP,
    name: 'Budget Workshop',
    icon: EverydollarIcon,
    group: 'Manage',
    isNew: true
  },
  {
    path: PATHS.FPU_DASHBOARD.COORDINATOR,
    name: 'My Impact',
    icon: AwardIcon,
    group: 'Results',
    end: true
  },
  {
    path: PATHS.FPU_DASHBOARD.REAL_STORIES,
    name: 'Real Stories',
    icon: HeartIcon,
    group: 'Results'
  },
  {
    path: PATHS.FPU_DASHBOARD.LAUNCH_AND_PROMOTE,
    name: 'Launch & Promote',
    icon: LightningIcon,
    group: 'Resources'
  },
  {
    path: PATHS.FPU_DASHBOARD.RESOURCES_AND_TRAINING,
    name: 'Resources & Training',
    icon: DocumentIcon,
    group: 'Resources'
  },
  {
    path: PATHS.FPU_DASHBOARD.WHATS_NEW,
    name: "What's New",
    icon: PlusIcon,
    group: 'Resources'
  },
  {
    path: PATHS.MEMBER_DASHBOARD.BASE,
    name: 'FPU (Member)',
    icon: FinancialPeaceIcon,
    iconClassName: 'text-blue-40',
    group: 'Products',
    largeIcon: true,
    target: '_blank'
  },
  {
    path: EVERYDOLLAR.BASE,
    name: 'EveryDollar',
    icon: EverydollarIcon,
    iconClassName: 'text-green-40',
    group: 'Products',
    largeIcon: true,
    target: '_blank'
  },
  {
    path: RPWA.HOME,
    name: 'Ramsey+',
    iconSrc: RamseyPlusLogo,
    group: 'Products',
    largeIcon: true,
    target: '_blank'
  },
  {
    path: PATHS.FPU_DASHBOARD.HELP_CENTER,
    name: 'Help Center',
    icon: HelpIcon,
    group: 'bottom',
    externalLink: true,
    target: '_blank'
  }
];

function NavLinkText({
  name,
  isActive,
  icon,
  iconSrc,
  iconClassName,
  largeIcon,
  isExternalLink,
  isNew,
  notify
}: {
  name: string;
  icon?: FC;
  iconSrc?: string;
  iconClassName?: string;
  largeIcon?: boolean;
  isActive: boolean;
  isExternalLink?: boolean;
  isNew?: boolean;
  notify?: boolean;
}) {
  return (
    <span
      className={`hover:bg-gray-20 py-inset-sm relative flex w-full items-center rounded-md pl-2 hover:text-blue-50 ${cn(
        { 'bg-white font-semibold text-blue-50': isActive }
      )}`}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          className={cn('mr-2 object-contain', {
            'h-[24px] w-[24px]': largeIcon,
            'h-[16px] w-[16px]': !largeIcon
          })}
        />
      ) : (
        icon && (
          <Icon
            icon={icon}
            size={largeIcon ? 'large' : 'medium'}
            className={cn('mr-2', iconClassName)}
          />
        )
      )}
      {name}
      {isNew && (
        <Tag color="primary" size="sm" className="absolute right-4 sm:-right-1">
          NEW
        </Tag>
      )}
      {notify && (
        <span className="bg-red-60 -mt-inset-sm ml-[6px] h-[10px] w-[10px] rounded-full" />
      )}
      {isExternalLink && (
        <Icon
          icon={LinkExternalIcon}
          size="small"
          className="ml-1.5 text-gray-50"
        />
      )}
    </span>
  );
}

function NavGroupSection({
  groupKey,
  label,
  defaultExpanded = false,
  hasActiveItem = false,
  hasNewItem = false,
  children
}: {
  groupKey: string;
  label: string;
  defaultExpanded?: boolean;
  hasActiveItem?: boolean;
  hasNewItem?: boolean;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [expandedGroups, setExpandedGroups] = useAtom(navGroupExpandedAtom);
  const userChoice = expandedGroups[groupKey];

  let isExpanded = defaultExpanded || hasActiveItem;
  if (userChoice) {
    const landedOnGroupPage = hasActiveItem && userChoice.pathname !== pathname;
    isExpanded = userChoice.isExpanded || landedOnGroupPage;
  }

  const showNewIndicator = hasNewItem && !isExpanded;
  const listId = `nav-group-${groupKey.toLowerCase()}`;

  return (
    <div className="pt-1">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={listId}
        aria-label={`${label}${showNewIndicator ? ', includes new items' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setExpandedGroups((prev) => ({
            ...prev,
            [groupKey]: { isExpanded: !isExpanded, pathname }
          }));
        }}
        className="text-gray-60 hover:text-gray-90 text-2 flex w-full cursor-pointer items-center justify-between rounded-md py-0.5 pl-2 font-semibold tracking-wider uppercase"
      >
        <span className="flex items-center">
          {label}
          {showNewIndicator && (
            <span
              aria-hidden
              data-testid="nav-group-new-indicator"
              className="ml-1 h-[6px] w-[6px] rounded-full bg-blue-50"
            />
          )}
        </span>
        <Icon
          icon={ChevronDownIcon}
          size="small"
          className={cn('transition-transform', { '-rotate-90': !isExpanded })}
        />
      </button>
      <div id={listId} hidden={!isExpanded}>
        {children}
      </div>
    </div>
  );
}

function FpuDashboardLayout({
  children,
  analyticsPageName,
  containerMaxWidthClassName
}: {
  children: ReactNode;
  analyticsPageName: string;
  containerMaxWidthClassName?: string;
}) {
  const { data: user } = useUserQuery();
  const hasOrgAdminRole = isOrganizationAdmin(user);
  const hasCoordinatorRole = isCoordinator(user);
  const hasSupportTeamRole = isSupportTeam(user);
  const { isEnabled: isBudgetWorkshopEnabled } = useBudgetWorkshopEnabled();
  const { data: hasRamseyPlusAccess } = useHasRamseyPlusAccess();

  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { orgIdForInternalUse } = useParams();
  const [organizationId, setOrganizationId] = useAtom(orgIdAtom);

  useZendeskWidget('7aaa9e0e-fd40-4fb4-af28-9ae4674d3206');

  const { hasNewArticles } = useHasNewArticles();

  useEffect(() => {
    const orgId = orgIdForInternalUse || searchParams.get('org_id');
    if (orgId) {
      setOrganizationId(orgId);
    }
  }, [orgIdForInternalUse, searchParams, setOrganizationId]);

  useEffect(() => {
    // Some dashboard pages can't show the org in their path, so the selected
    // org is kept in the URL as ?org_id= instead. Skip that when the path
    // already names the org: organizationId (shared Jotai state) is still the
    // previous org for one render, so it would append the wrong id.
    if (orgIdForInternalUse) return;

    if (
      organizationId &&
      pathname !== `${PATHS.FPU_DASHBOARD.ORGANIZATION}/${organizationId}`
    ) {
      setSearchParams({ org_id: organizationId });
    }
  }, [orgIdForInternalUse, organizationId, pathname, setSearchParams]);

  let roleTag: RoleTagText | null = null;
  if (hasOrgAdminRole) {
    roleTag = 'Administrator';
  } else if (hasCoordinatorRole) {
    roleTag = 'Coordinator';
  }

  const visibleItems = DASHBOARD_NAVIGATION_ITEMS.flatMap((item): NavItem[] => {
    if (item.path === PATHS.FPU_DASHBOARD.BUDGET_WORKSHOP) {
      return isBudgetWorkshopEnabled && (hasOrgAdminRole || hasSupportTeamRole)
        ? [item]
        : [];
    }

    if (item.path === RPWA.HOME) {
      return hasRamseyPlusAccess ? [item] : [];
    }

    if (!hasOrgAdminRole && !hasSupportTeamRole) {
      if (
        item.path === PATHS.FPU_DASHBOARD.ORGANIZATION ||
        item.path === PATHS.FPU_DASHBOARD.MY_PEOPLE ||
        item.path === PATHS.FPU_DASHBOARD.SEND_MEMBERSHIPS
      ) {
        return [];
      }

      // Coordinators without an admin role see their dashboard as "Home"
      if (item.path === PATHS.FPU_DASHBOARD.COORDINATOR) {
        return [{ ...item, name: 'Home', icon: HomeIcon, group: 'top' }];
      }
    }

    if (
      hasOrgAdminRole &&
      !hasCoordinatorRole &&
      item.path === PATHS.FPU_DASHBOARD.COORDINATOR
    ) {
      return [];
    }

    return [item];
  });
  const topItems = visibleItems.filter((item) => item.group === 'top');
  const bottomItems = visibleItems.filter((item) => item.group === 'bottom');

  const renderLink = (items: NavItem[]) =>
    items.map((item) => (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.end}
        {...(item.target && {
          target: item.target,
          rel: 'noopener noreferrer'
        })}
        className="text-gray-70 flex w-full py-0.5"
        onClick={() =>
          analyticsLogger.trackElementClicked({
            element: 'FPU Dashboard Navigation',
            label: `${item.name}`
          })
        }
      >
        {({ isActive }) => (
          <NavLinkText
            icon={item.icon}
            iconSrc={item.iconSrc}
            iconClassName={item.iconClassName}
            largeIcon={item.largeIcon}
            name={item.name}
            isActive={isActive}
            isExternalLink={item.externalLink}
            isNew={item.isNew}
            notify={
              hasNewArticles && item.path === PATHS.FPU_DASHBOARD.WHATS_NEW
            }
          />
        )}
      </NavLink>
    ));

  return (
    <PageAnalyticsWrapper category="FPU Dashboard" page={analyticsPageName}>
      <div className="flex min-h-screen flex-col">
        <div className="w-full grow">
          <Header withNavigation roleTag={roleTag} />
          <SideNavigation roleTag={roleTag}>
            <div className="flex min-h-full flex-col">
              {renderLink(topItems)}
              {COLLAPSIBLE_GROUPS.map((group) => {
                const items = visibleItems.filter(
                  (item) => item.group === group
                );
                if (items.length === 0) return null;

                return (
                  <NavGroupSection
                    key={group}
                    groupKey={group}
                    label={GROUP_LABELS[group]}
                    defaultExpanded={group === 'Manage'}
                    hasActiveItem={items.some(
                      (item) =>
                        item.target !== '_blank' &&
                        matchPath(
                          { path: item.path, end: !!item.end },
                          pathname
                        )
                    )}
                    hasNewItem={items.some((item) => item.isNew)}
                  >
                    {renderLink(items)}
                  </NavGroupSection>
                );
              })}
              <div className="mt-auto">{renderLink(bottomItems)}</div>
            </div>
          </SideNavigation>

          <div className="lg:pr-8 lg:pl-36">
            <Container
              className="px-2 pt-4 pb-5 sm:px-3 lg:px-0"
              maxWidthClassName={containerMaxWidthClassName}
            >
              {children}
            </Container>
          </div>
        </div>
        <div className="lg:pr-8 lg:pl-36">
          <PageFooter className="mx-auto max-w-screen-lg pb-2!" />
        </div>
      </div>
    </PageAnalyticsWrapper>
  );
}

export default FpuDashboardLayout;
